import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import CertificateField from './CertificateField';
import {
  findReceivingCertificateDocument,
  findInventoryCertificateDocument,
  downloadReceivingCertificate,
  downloadInventoryCertificate
} from '../../utils/storeCertificateDocuments';
import {
  PART_UNIT_CONDITIONS,
  PART_CONDITION_LABELS,
  formatPartConditionLabel
} from '../../constants/partUnitConditions';
import { isLoanInBorrowReceiving } from '../../utils/loanReceivingProfiles';

function loanLineConditionCodes(lineItem) {
  const set = new Set();
  const c = String(lineItem?.condition || '').trim().toUpperCase();
  if (c) set.add(c);
  (Array.isArray(lineItem?.unitDetails) ? lineItem.unitDetails : []).forEach((u) => {
    const uc = String(u?.condition || '').trim().toUpperCase();
    if (uc) set.add(uc);
  });
  return [...set];
}

function formatConditionSummary(codes) {
  if (!codes.length) return '—';
  return codes.map((code) => formatPartConditionLabel(code)).join(', ');
}

function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysBetweenToday(dateLike) {
  const date = toDate(dateLike);
  if (!date) return null;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

function dueLabel(returnDueDate, outstandingQty) {
  if (outstandingQty <= 0) return 'Returned';
  const days = daysBetweenToday(returnDueDate);
  if (days == null) return 'N/A';
  if (days < 0) return `Overdue ${Math.abs(days)}d`;
  if (days === 0) return 'Due today';
  return `${days}d left`;
}

function dueColor(returnDueDate, outstandingQty) {
  if (!returnDueDate || outstandingQty <= 0) return 'default';
  const days = daysBetweenToday(returnDueDate);
  if (days == null) return 'default';
  if (days < 0) return 'error';
  if (days <= 7) return 'warning';
  return 'success';
}

function summarizeLoanItems(items, loanType, { recordDocuments, recordId, rawItems } = {}) {
  const lines = Array.isArray(items) ? items : [];
  return lines.map((item, idx) => {
    const quantity = loanType === 'LOAN_IN'
      ? Number(item.receivedQuantity || item.quantity || 0)
      : Number(item.quantity || 0);
    const returnedQty = Number(item.quantityReturned || 0);
    const outstandingQty = Math.max(0, quantity - returnedQty);
    const conditionCodes = loanLineConditionCodes(item);
    const rawItem = Array.isArray(rawItems) ? rawItems[idx] : null;
    const inventoryItemId = rawItem?.inventoryItemId?._id || rawItem?.inventoryItemId || null;
    const inventoryDocs = rawItem?.inventoryItemId?.documents || [];
    const certificateDoc = loanType === 'LOAN_IN'
      ? findReceivingCertificateDocument(recordDocuments, item.partNumber)
      : findInventoryCertificateDocument(
        inventoryDocs,
        item.partNumber || rawItem?.inventoryItemId?.partNumber
      );
    return {
      lineIndex: idx,
      conditionCodes,
      partNumber: item.partNumber || '',
      description: loanType === 'LOAN_IN'
        ? (item.description || '')
        : (item.inventoryItemId?.description || item.description || ''),
      conditionSummary: formatConditionSummary(conditionCodes),
      quantity,
      returnedQty,
      outstandingQty,
      storeType: item.storeType || '',
      locationText: item.locationText || '',
      certificateNumber: item.certificateNumber
        || rawItem?.inventoryItemId?.certificateNumber
        || '',
      certificateDoc,
      receivingRecordId: loanType === 'LOAN_IN' ? recordId : null,
      inventoryItemId,
      batchNumber: item.batchNumber || '',
      serialNumber: item.serialNumber || '',
      shelfLifeUpto: item.shelfLifeUpto || null,
      remarks: item.remarks || '',
      unitDetails: Array.isArray(item.unitDetails) ? item.unitDetails : []
    };
  });
}

function recordTotals(itemSummaries) {
  return itemSummaries.reduce(
    (acc, line) => ({
      quantity: acc.quantity + line.quantity,
      returnedQty: acc.returnedQty + line.returnedQty,
      outstandingQty: acc.outstandingQty + line.outstandingQty
    }),
    { quantity: 0, returnedQty: 0, outstandingQty: 0 }
  );
}

function normalizeLoanInRecord(record) {
  if (!isLoanInBorrowReceiving(record)) return null;
  const itemSummaries = summarizeLoanItems(record.items, 'LOAN_IN', {
    recordDocuments: record.documents,
    recordId: record._id
  });
  const totals = recordTotals(itemSummaries);
  const hasOutstanding = totals.outstandingQty > 0;
  return {
    id: String(record._id),
    loanType: 'LOAN_IN',
    counterpartyCompany: record.counterpartyCompany || '',
    loanReferenceNumber: record.loanReferenceNumber || '',
    loanDate: record.receivingDate || record.createdAt,
    returnDueDate: record.returnDueDate || null,
    approvalStatus: record.workflowStatus || 'APPROVED',
    status: record.returnStatus || (hasOutstanding ? 'OPEN' : 'RETURNED'),
    sourceInventoryDomain: 'EXTERNAL',
    supplierName: record.supplier?.name || record.supplier?.code || '',
    discrepancies: record.discrepancies || '',
    items: itemSummaries,
    totals
  };
}

function normalizeLoanOutRecord(record) {
  const itemSummaries = summarizeLoanItems(record.items, 'LOAN_OUT', {
    rawItems: record.items
  });
  const totals = recordTotals(itemSummaries);
  const hasOutstanding = totals.outstandingQty > 0;
  return {
    id: String(record._id),
    loanType: 'LOAN_OUT',
    counterpartyCompany: record.counterpartyCompany || '',
    loanReferenceNumber: record.loanReferenceNumber || '',
    loanDate: record.issuedDate || record.createdAt,
    returnDueDate: record.returnDueDate || null,
    approvalStatus: record.approvalStatus || 'NOT_REQUIRED',
    status: record.status || (hasOutstanding ? 'ISSUED' : 'RETURNED'),
    sourceInventoryDomain: record.sourceInventoryDomain || 'STORE',
    supplierName: '',
    discrepancies: record.deliveryNote || '',
    items: itemSummaries,
    totals
  };
}

const LoanInventoryModule = ({
  title = 'Loan Inventory',
  infoMessage = 'Loan inventory tracks active borrowed/loaned lines with due dates and outstanding balances.',
  emptyMessage = 'No loan inventory records found',
  loadErrorMessage = 'Failed to load loan inventory summary'
}) => {
  const { showToast } = useToast();
  const [records, setRecords] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [directionFilter, setDirectionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [conditionFilter, setConditionFilter] = useState('ALL');
  const [downloadingCertificateKey, setDownloadingCertificateKey] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [receivingRes, issuanceRes] = await Promise.all([
        axios.get('/api/store/receiving', {
          params: { loanDirection: 'LOAN_IN', workflowStatus: 'APPROVED', loanBorrowOnly: 'true' }
        }),
        axios.get('/api/store/loan/issuances', { params: { loanDirection: 'LOAN_OUT' } })
      ]);

      const loanInRecords = (Array.isArray(receivingRes.data) ? receivingRes.data : [])
        .map(normalizeLoanInRecord)
        .filter(Boolean);
      const loanOutRecords = (Array.isArray(issuanceRes.data) ? issuanceRes.data : [])
        .map(normalizeLoanOutRecord);

      setRecords([...loanOutRecords, ...loanInRecords]);
    } catch (error) {
      console.error(loadErrorMessage, error);
      showToast(loadErrorMessage, 'error');
    }
  }, [showToast, loadErrorMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleRowExpansion = (recordId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  const handleLoanCertificateDownload = async (record, item) => {
    if (!item?.certificateDoc?._id || downloadingCertificateKey) return;
    const key = `${record.id}:${item.lineIndex}:${item.certificateDoc._id}`;
    setDownloadingCertificateKey(key);
    try {
      if (record.loanType === 'LOAN_IN' && item.receivingRecordId) {
        await downloadReceivingCertificate(item.receivingRecordId, item.certificateDoc);
      } else if (item.inventoryItemId) {
        await downloadInventoryCertificate(item.inventoryItemId, item.certificateDoc, undefined, 'LOAN');
      }
    } catch (error) {
      showToast(error.message || error.response?.data?.message || 'Failed to download certificate', 'error');
    } finally {
      setDownloadingCertificateKey('');
    }
  };

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      if (directionFilter !== 'ALL' && record.loanType !== directionFilter) return false;
      if (statusFilter !== 'ALL' && record.status !== statusFilter) return false;
      if (conditionFilter !== 'ALL') {
        const hasCondition = record.items.some((line) =>
          (line.conditionCodes || []).includes(conditionFilter)
        );
        if (!hasCondition) return false;
      }
      if (!searchTerm.trim()) return true;
      const term = searchTerm.trim().toLowerCase();
      const recordMatch =
        String(record.counterpartyCompany || '').toLowerCase().includes(term) ||
        String(record.loanReferenceNumber || '').toLowerCase().includes(term) ||
        String(record.supplierName || '').toLowerCase().includes(term);
      if (recordMatch) return true;
      return record.items.some((line) =>
        String(line.partNumber || '').toLowerCase().includes(term) ||
        String(line.referencePartNumber || '').toLowerCase().includes(term) ||
        String(line.description || '').toLowerCase().includes(term)
      );
    });
  }, [records, directionFilter, statusFilter, conditionFilter, searchTerm]);

  const stats = useMemo(() => {
    const allLines = records.flatMap((record) => record.items);
    const openLines = allLines.filter((line) => line.outstandingQty > 0);
    const overdueLines = records.filter((record) => {
      const days = daysBetweenToday(record.returnDueDate);
      return record.totals.outstandingQty > 0 && days != null && days < 0;
    });
    return {
      openLines: openLines.length,
      overdueLines: overdueLines.length,
      totalOutstandingQty: openLines.reduce((sum, line) => sum + Number(line.outstandingQty || 0), 0)
    };
  }, [records]);

  const historyColSpan = 12;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {infoMessage}
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Open Loan Lines</Typography>
            <Typography variant="h6">{stats.openLines}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Overdue Loans</Typography>
            <Typography variant="h6" color={stats.overdueLines > 0 ? 'error.main' : 'text.primary'}>{stats.overdueLines}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Outstanding Quantity</Typography>
            <Typography variant="h6">{stats.totalOutstandingQty}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Part no, recipient, reference"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Loan Type</InputLabel>
              <Select
                label="Loan Type"
                value={directionFilter}
                onChange={(e) => setDirectionFilter(e.target.value)}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="LOAN_IN">Loan In</MenuItem>
                <MenuItem value="LOAN_OUT">Loan Out</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="ISSUED">Issued</MenuItem>
                <MenuItem value="PARTIAL_RETURN">Partial Return</MenuItem>
                <MenuItem value="RETURNED">Returned</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Condition</InputLabel>
              <Select
                label="Condition"
                value={conditionFilter}
                onChange={(e) => setConditionFilter(e.target.value)}
              >
                <MenuItem value="ALL">All conditions</MenuItem>
                {PART_UNIT_CONDITIONS.map((v) => (
                  <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>Loan Date</TableCell>
              <TableCell>Loan Type</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Return Due</TableCell>
              <TableCell>Due Status</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Outstanding</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.map((record) => (
              <React.Fragment key={record.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleRowExpansion(record.id)} aria-label="Toggle item details">
                      {expandedRows[record.id] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {record.loanDate ? new Date(record.loanDate).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={record.loanType} />
                  </TableCell>
                  <TableCell>{record.counterpartyCompany || '—'}</TableCell>
                  <TableCell>{record.loanReferenceNumber || '—'}</TableCell>
                  <TableCell>
                    {record.returnDueDate ? new Date(record.returnDueDate).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={dueColor(record.returnDueDate, record.totals.outstandingQty)}
                      label={dueLabel(record.returnDueDate, record.totals.outstandingQty)}
                    />
                  </TableCell>
                  <TableCell>{record.items.length} item(s)</TableCell>
                  <TableCell>{record.totals.quantity}</TableCell>
                  <TableCell>{record.totals.outstandingQty}</TableCell>
                  <TableCell>{record.approvalStatus || '—'}</TableCell>
                  <TableCell>{record.status || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={historyColSpan} sx={{ py: 0 }}>
                    <Collapse in={expandedRows[record.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {record.loanType === 'LOAN_IN' && record.supplierName && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Supplier</Typography>
                              <Typography variant="body2">{record.supplierName}</Typography>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">Source</Typography>
                            <Typography variant="body2">{record.sourceInventoryDomain || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">Returned Qty (total)</Typography>
                            <Typography variant="body2">{record.totals.returnedQty}</Typography>
                          </Grid>
                        </Grid>

                        <Typography variant="subtitle2" gutterBottom>
                          Loan Items
                        </Typography>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                          <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>Part Number</TableCell>
                                <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>Reference Part Number</TableCell>
                                <TableCell sx={{ minWidth: 180 }}>Description</TableCell>
                                <TableCell>Condition</TableCell>
                                <TableCell>Qty</TableCell>
                                <TableCell>Returned</TableCell>
                                <TableCell>Outstanding</TableCell>
                                <TableCell>Store Type</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Certificate</TableCell>
                                <TableCell>Batch / Serial</TableCell>
                                <TableCell>Shelf Life</TableCell>
                                <TableCell>Remarks</TableCell>
                                <TableCell>Unit Details</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {record.items.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={14} align="center">
                                    <Typography variant="body2" color="text.secondary">No items on this loan</Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                record.items.map((item) => (
                                  <TableRow key={`${record.id}-${item.lineIndex}`}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                      {item.partNumber || '—'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                      {item.referencePartNumber || '—'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 280 }}>
                                      {item.description || '—'}
                                    </TableCell>
                                    <TableCell>{item.conditionSummary || '—'}</TableCell>
                                    <TableCell>{item.quantity}</TableCell>
                                    <TableCell>{item.returnedQty}</TableCell>
                                    <TableCell>{item.outstandingQty}</TableCell>
                                    <TableCell>{item.storeType || '—'}</TableCell>
                                    <TableCell>{item.locationText || '—'}</TableCell>
                                    <TableCell>
                                      <CertificateField
                                        certificateNumber={item.certificateNumber}
                                        document={item.certificateDoc}
                                        onDownload={
                                          item.certificateDoc
                                            ? () => handleLoanCertificateDownload(record, item)
                                            : null
                                        }
                                        downloading={
                                          downloadingCertificateKey
                                            === `${record.id}:${item.lineIndex}:${item.certificateDoc?._id}`
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {[item.batchNumber, item.serialNumber].filter(Boolean).join(' / ') || '—'}
                                    </TableCell>
                                    <TableCell>
                                      {item.shelfLifeUpto ? new Date(item.shelfLifeUpto).toLocaleDateString() : '—'}
                                    </TableCell>
                                    <TableCell>{item.remarks || '—'}</TableCell>
                                    <TableCell>
                                      {item.unitDetails.length > 0 ? (
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                          {item.unitDetails.map((unit, unitIdx) => (
                                            <Typography key={unitIdx} component="li" variant="caption" color="text.secondary">
                                              {formatPartConditionLabel(unit.condition)}
                                              {unit.batchNumber ? ` · Batch ${unit.batchNumber}` : ''}
                                              {unit.serialNumber ? ` · SN ${unit.serialNumber}` : ''}
                                            </Typography>
                                          ))}
                                        </Box>
                                      ) : (
                                        '—'
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {record.discrepancies && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {record.loanType === 'LOAN_OUT' ? 'Issue Note' : 'Notes / Discrepancies'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {record.discrepancies}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {filteredRecords.length === 0 && (
              <TableRow>
                <TableCell colSpan={historyColSpan} align="center">
                  <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LoanInventoryModule;
